import type { AppLocale } from "@/lib/i18n/locale";

export const NATIVE_APP_PROMOTION_STORAGE_KEY = "meongnyang.native-app-promotion.v1";
export const NATIVE_APP_PROMOTION_SESSION_KEY = "meongnyang.native-app-promotion.session.v1";
export const LEGACY_PWA_INSTALL_STORAGE_KEY = "meongnyang.pwa-install";
export const NATIVE_APP_PROMOTION_SOFT_DISMISS_DAYS = 7;
export const NATIVE_APP_PROMOTION_ATTEMPT_DAYS = 14;
export const NATIVE_APP_PROMOTION_STORE_CLICK_DAYS = 14;

export const NATIVE_APP_HOME_URL = "mypetroutine://app";
export const NATIVE_APP_STORE_URLS = {
  ios: "https://apps.apple.com/app/id6789836323",
  android: "https://play.google.com/store/apps/details?id=io.petnow.mypetroutine.codex",
} as const;

export const NATIVE_APP_PROMOTION_CONFIG = {
  enabled: true,
  platforms: {
    ios: true,
    android: true,
  },
  locales: {
    ko: true,
    en: true,
  },
} as const;

export type NativeAppPromotionSurface =
  | "onboarding_prelogin"
  | "app_home"
  | "settings";

export type NativeAppPromotionState = {
  version: 1;
  dismissedUntil: string | null;
  launchAttemptedUntil: string | null;
  storeClickedUntil: string | null;
  lastShownAt: string | null;
};

export type NativeAppPromotionRuntime = {
  userAgent: string;
  isMobileEligible: boolean;
  isAndroid: boolean;
  isIos: boolean;
  isIosDesktopMode: boolean;
  isStandalone: boolean;
  isInAppBrowser: boolean;
  isMetaInAppBrowser: boolean;
  browser: "chrome" | "samsung" | "safari" | "other";
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const DEFAULT_STATE: NativeAppPromotionState = {
  version: 1,
  dismissedUntil: null,
  launchAttemptedUntil: null,
  storeClickedUntil: null,
  lastShownAt: null,
};

const ANDROID_PATTERN = /android/i;
const IOS_PATTERN = /iphone|ipad|ipod/i;
const IOS_DESKTOP_PATTERN = /macintosh|mac os x/i;
const MOBILE_PATTERN = /android|iphone|ipad|ipod|mobile/i;
const IN_APP_BROWSER_PATTERN = /instagram|fban|fbav|fb_iab|fbios|kakaotalk|naver|line\//i;
const META_IN_APP_BROWSER_PATTERN = /instagram|fban|fbav|fb_iab|fbios/i;
const CHROME_PATTERN = /chrome|crios/i;
const SAMSUNG_PATTERN = /samsungbrowser/i;
const SAFARI_PATTERN = /safari/i;
const IOS_NON_SAFARI_PATTERN = /crios|fxios|edgios|optios/i;

export function createDefaultNativeAppPromotionState(): NativeAppPromotionState {
  return { ...DEFAULT_STATE };
}

export function isFutureIsoTimestamp(value: string | null, now = new Date()) {
  if (!value) {
    return false;
  }

  const timestamp = Date.parse(value);
  return !Number.isNaN(timestamp) && timestamp > now.getTime();
}

export function addNativeAppPromotionDays(now: Date, days: number) {
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}

function normalizeIsoTimestamp(value: unknown) {
  return typeof value === "string" && !Number.isNaN(Date.parse(value)) ? value : null;
}

function normalizeState(value: unknown): NativeAppPromotionState {
  if (!value || typeof value !== "object") {
    return createDefaultNativeAppPromotionState();
  }

  const candidate = value as Partial<NativeAppPromotionState> & { version?: unknown };
  return {
    version: 1,
    dismissedUntil: normalizeIsoTimestamp(candidate.dismissedUntil),
    launchAttemptedUntil: normalizeIsoTimestamp(candidate.launchAttemptedUntil),
    storeClickedUntil: normalizeIsoTimestamp(candidate.storeClickedUntil),
    lastShownAt: normalizeIsoTimestamp(candidate.lastShownAt),
  };
}

function readLegacyDismissedUntil(value: unknown, now: Date) {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const dates = [candidate.softDismissedUntil, candidate.promptDismissedUntil]
    .filter((entry): entry is string => typeof entry === "string")
    .map((entry) => new Date(entry))
    .filter((entry) => !Number.isNaN(entry.getTime()) && entry.getTime() > now.getTime())
    .sort((left, right) => right.getTime() - left.getTime());

  if (!dates[0]) {
    return null;
  }

  const maxMigrationUntil = new Date(now.getTime() + NATIVE_APP_PROMOTION_ATTEMPT_DAYS * 24 * 60 * 60 * 1000);
  return new Date(Math.min(dates[0].getTime(), maxMigrationUntil.getTime())).toISOString();
}

export function readNativeAppPromotionState(
  storage?: StorageLike | null,
  now = new Date(),
): NativeAppPromotionState {
  const targetStorage = storage ?? (typeof window !== "undefined" ? window.localStorage : null);
  if (!targetStorage) {
    return createDefaultNativeAppPromotionState();
  }

  try {
    const raw = targetStorage.getItem(NATIVE_APP_PROMOTION_STORAGE_KEY);
    if (raw) {
      return normalizeState(JSON.parse(raw));
    }

    const legacyRaw = targetStorage.getItem(LEGACY_PWA_INSTALL_STORAGE_KEY);
    const migrated: NativeAppPromotionState = {
      ...createDefaultNativeAppPromotionState(),
      dismissedUntil: legacyRaw
        ? readLegacyDismissedUntil(JSON.parse(legacyRaw), now)
        : null,
    };
    // Persist the migration immediately. The legacy key remains available for rollback.
    try {
      targetStorage.setItem(NATIVE_APP_PROMOTION_STORAGE_KEY, JSON.stringify(migrated));
    } catch {
      // A blocked storage area must not prevent the CTA from rendering.
    }
    return migrated;
  } catch {
    return createDefaultNativeAppPromotionState();
  }
}

export function persistNativeAppPromotionState(
  state: NativeAppPromotionState,
  storage?: StorageLike | null,
) {
  const targetStorage = storage ?? (typeof window !== "undefined" ? window.localStorage : null);
  if (!targetStorage) {
    return;
  }

  try {
    targetStorage.setItem(NATIVE_APP_PROMOTION_STORAGE_KEY, JSON.stringify(normalizeState(state)));
  } catch {
    // Storage failures are intentionally isolated from rendering and navigation.
  }
}

export function markNativeAppPromotionShown(state: NativeAppPromotionState, now = new Date()) {
  return { ...state, lastShownAt: now.toISOString() };
}

export function dismissNativeAppPromotion(state: NativeAppPromotionState, now = new Date()) {
  return {
    ...state,
    dismissedUntil: addNativeAppPromotionDays(now, NATIVE_APP_PROMOTION_SOFT_DISMISS_DAYS),
  };
}

export function markNativeAppLaunchAttempted(state: NativeAppPromotionState, now = new Date()) {
  return {
    ...state,
    launchAttemptedUntil: addNativeAppPromotionDays(now, NATIVE_APP_PROMOTION_ATTEMPT_DAYS),
  };
}

export function markNativeAppStoreClicked(state: NativeAppPromotionState, now = new Date()) {
  return {
    ...state,
    storeClickedUntil: addNativeAppPromotionDays(now, NATIVE_APP_PROMOTION_STORE_CLICK_DAYS),
  };
}

export function shouldShowNativeAppPromotion(
  state: NativeAppPromotionState,
  now = new Date(),
) {
  return ![
    state.dismissedUntil,
    state.launchAttemptedUntil,
    state.storeClickedUntil,
  ].some((value) => isFutureIsoTimestamp(value, now));
}

export function detectNativeAppPromotionRuntime(
  params: {
    hasWindow?: boolean;
    matchMediaMatches?: boolean;
    navigatorStandalone?: boolean;
    maxTouchPoints?: number;
    userAgent?: string;
  } = {},
): NativeAppPromotionRuntime {
  const windowObject = typeof window !== "undefined" ? window : undefined;
  const hasWindow = params.hasWindow ?? Boolean(windowObject);
  const userAgent = params.userAgent ?? (
    hasWindow && typeof windowObject?.navigator?.userAgent === "string"
      ? windowObject.navigator.userAgent
      : ""
  );
  const maxTouchPoints = params.maxTouchPoints ?? (
    hasWindow && typeof windowObject?.navigator?.maxTouchPoints === "number"
      ? windowObject.navigator.maxTouchPoints
      : 0
  );
  const isAndroid = ANDROID_PATTERN.test(userAgent);
  const isIos = IOS_PATTERN.test(userAgent);
  const isIosDesktopMode = !isAndroid
    && IOS_DESKTOP_PATTERN.test(userAgent)
    && maxTouchPoints > 1;
  const isInAppBrowser = IN_APP_BROWSER_PATTERN.test(userAgent);
  const isMetaInAppBrowser = META_IN_APP_BROWSER_PATTERN.test(userAgent);
  const matchMediaMatches = params.matchMediaMatches ?? (
    hasWindow
      && typeof windowObject?.matchMedia === "function"
      && windowObject.matchMedia("(display-mode: standalone)").matches
  );
  const navigatorStandalone = params.navigatorStandalone ?? (
    hasWindow && windowObject?.navigator && "standalone" in windowObject.navigator
      ? Boolean((windowObject.navigator as Navigator & { standalone?: boolean }).standalone)
      : false
  );
  const isStandalone = Boolean(matchMediaMatches || navigatorStandalone);
  const browser = SAMSUNG_PATTERN.test(userAgent)
    ? "samsung"
    : CHROME_PATTERN.test(userAgent)
      ? "chrome"
      : SAFARI_PATTERN.test(userAgent) && !IOS_NON_SAFARI_PATTERN.test(userAgent)
        ? "safari"
        : "other";

  return {
    userAgent,
    isAndroid,
    isIos: isIos || isIosDesktopMode,
    isIosDesktopMode,
    isMobileEligible: isAndroid || isIos || isIosDesktopMode || MOBILE_PATTERN.test(userAgent),
    isStandalone,
    isInAppBrowser,
    isMetaInAppBrowser,
    browser,
  };
}

export function isNativeAppPromotionEnabled(
  locale: AppLocale,
  runtime: NativeAppPromotionRuntime,
) {
  if (!NATIVE_APP_PROMOTION_CONFIG.enabled || !NATIVE_APP_PROMOTION_CONFIG.locales[locale]) {
    return false;
  }

  if (runtime.isAndroid) {
    return NATIVE_APP_PROMOTION_CONFIG.platforms.android;
  }

  if (runtime.isIos) {
    return NATIVE_APP_PROMOTION_CONFIG.platforms.ios;
  }

  // Desktop and unknown browsers can still reach the manual surface and choose
  // a store. Automatic in-app promotion is gated separately by the provider.
  return NATIVE_APP_PROMOTION_CONFIG.platforms.ios || NATIVE_APP_PROMOTION_CONFIG.platforms.android;
}

export function getNativeAppStoreUrl(runtime: NativeAppPromotionRuntime) {
  if (runtime.isAndroid) {
    return NATIVE_APP_STORE_URLS.android;
  }

  if (runtime.isIos) {
    return NATIVE_APP_STORE_URLS.ios;
  }

  return null;
}

export function buildNativeAppLaunchHref(runtime: NativeAppPromotionRuntime) {
  if (runtime.isAndroid && runtime.browser === "chrome" && !runtime.isInAppBrowser) {
    return `intent://app#Intent;scheme=mypetroutine;package=io.petnow.mypetroutine.codex;S.browser_fallback_url=${encodeURIComponent(NATIVE_APP_STORE_URLS.android)};end`;
  }

  return NATIVE_APP_HOME_URL;
}
